import React, { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import {
  FaArrowLeft,
  FaClock,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhoneAlt,
  FaStore,
  FaUniversity,
} from "react-icons/fa";
import EditOutletScreen from "./EditOutletScreen";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Message from "../components/Message";
import Loader from "../components/Loader";
import ReUsableModal from "../components/ReUsableModal";
import {
  useAddFoodItemCountMutation,
  useGetFoodItemsByOutletIdQuery,
} from "../slices/FoodItemSilce";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  useEditOutletDetailsMutation,
  useGetOutletDetailsQuery,
} from "../slices/OutletSlice";
import {
  useAddAdminToOutletMutation,
  useGetAllAdminsQuery,
} from "../slices/AdminSilce";
import { setCredentials } from "../slices/authSlice";
import {
  canAccess,
  isOwner,
  outletAdminLimit,
  outletAdminsOf,
} from "../utils/adminAccess";

const YesNoPill = ({ value }) => (
  <span className={`od-pill ${value ? "od-pill-yes" : "od-pill-no"}`}>
    {value ? "Yes" : "No"}
  </span>
);

const DetailRow = ({ label, children }) => (
  <div className="od-row">
    <span className="od-label">{label}</span>
    <span className="od-value">{children}</span>
  </div>
);

const OutletDetailsScreen = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const { outletId } = useParams();

  const [isEditable, setIsEditable] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [itemName, setItemName] = useState("");
  const [quantity, setQuantity] = useState("");
  const [addQuantity, setAddQuantity] = useState("");
  const [newQuantity, setNewQuantity] = useState("");
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showItemCountModal, setShowItemCountModal] = useState(false);
  const [showAdminModal, setShowAdmilModal] = useState(false);
  const [adminMobile, setAdminMobile] = useState("");

  const {
    data: outletDetails,
    isLoading,
    error,
    refetch,
  } = useGetOutletDetailsQuery({ id: outletId, token: userInfo.token });

  const {
    data: foodItems,
    isLoading: foodItemsLoading,
    error: foodItemsError,
    refetch: foodItemRefetch,
  } = useGetFoodItemsByOutletIdQuery({
    token: userInfo?.token,
    outletId: outletId,
  });

  const [otLocation, setOtLocation] = useState(null);

  useEffect(() => {
    if (
      outletDetails?.outletLocation?.coordinates?.length === 2 &&
      outletDetails?.outletLocation?.coordinates[0] &&
      outletDetails?.outletLocation?.coordinates[1]
    ) {
      setOtLocation(
        `https://www.google.com/maps?q=${outletDetails.outletLocation?.coordinates[0]},${outletDetails?.outletLocation?.coordinates[1]}`
      );
    } else {
      setOtLocation(null);
    }
  }, [outletDetails]); // runs every time outletDetails changes

  const [locationError, setLocationError] = useState(null);

  const [addFoodItemCount, { isLoading: addingCount }] =
    useAddFoodItemCountMutation();

  const [addAdmin, { isLoading: addAdminLoading }] =
    useAddAdminToOutletMutation();

  // Only owners manage admins, and only they may list the company's admins
  const { data: companyAdmins, refetch: refetchCompanyAdmins } =
    useGetAllAdminsQuery(
      { token: userInfo?.token },
      { skip: !isOwner(userInfo) }
    );
  const outletCurrentAdmins = outletAdminsOf(companyAdmins, outletId);
  const outletAdminLimitValue = outletAdminLimit(outletDetails);
  const outletAtAdminLimit =
    outletCurrentAdmins.length >= outletAdminLimitValue;
  const assignableAdmins = (Array.isArray(companyAdmins) ? companyAdmins : [])
    .filter((admin) => admin.adminType === "Admin")
    .filter(
      (admin) => !outletCurrentAdmins.some((current) => current._id === admin._id)
    );

  const outletFoodItems = foodItems;

  const dispatch = useDispatch();

  useEffect(() => {
    if (!isEditable) {
      if (outletDetails && !isLoading && !error) {
        refetch();
        const olDetails = isLoading ? {} : outletDetails ? outletDetails : {};
        let excludeCurrentOulet = userInfo?.outletId.filter(
          (od) => od?._id !== olDetails?._id
        );
        excludeCurrentOulet.push(olDetails);
        dispatch(
          setCredentials({ ...userInfo, outletId: excludeCurrentOulet })
        );
      }
    }
  }, [isEditable, outletDetails, isLoading, refetch, error]);

  useEffect(() => {
    if (quantity != null && addQuantity != null) {
      setNewQuantity(Number(quantity) + Number(addQuantity));
    }
  }, [quantity, addQuantity]);

  const handleEdit = () => {
    setIsEditable(true);
  };

  // const outletCancelEdit = () => {
  //   setIsEditable(false);
  // };

  const addItemHandler = (item) => {
    setItemName(item?.itemName);
    setQuantity(item?.itemQuantity);
    setNewQuantity(item?.itemQuantity);
    setSelectedItemId(item._id);
    setShowItemCountModal(true);
  };

  const submitAddFoodItem = async () => {
    try {
      const data = {
        _id: selectedItemId,
        itemCount: addQuantity,
        token: userInfo.token,
      };

      await addFoodItemCount(data).unwrap();
      await foodItemRefetch();
      setShowItemCountModal(false);
      toast.success(" Item quantity updated Successfully");
    } catch (err) {
      console.error("Error adding food count:", err);
    }
  };

  const handleCancelEdit = () => {
    setAddQuantity("");
    setNewQuantity("");
    setShowItemCountModal(false);
  };

  const handleNavigateToEdit = (item) => {
    console.log("Outlet Details before navigate", outletDetails);
    navigate(`/editFoodItem/${item._id}`, {
      state: { foodItem: item, outlet: outletDetails },
    });
  };

  const handleAddAdmin = () => {
    setAdminMobile("");
    refetchCompanyAdmins();
    setShowAdmilModal(true);
  };

  const handleAdminCancel = () => {
    setShowAdmilModal(false);
  };

  const handleAddAdminSubmit = async () => {
    if (outletAtAdminLimit) {
      toast.error(
        `This outlet already has the maximum number of admins (${outletAdminLimitValue}). Please contact Zutle to add more admins.`
      );
      return;
    }
    if (!adminMobile) {
      toast.error("Please select an admin");
      return;
    }
    try {
      await addAdmin({
        outletId: outletId,
        mobileNumber: adminMobile,
        token: userInfo?.token,
      }).unwrap();
      toast.success("Admin assigned Successfully");
      setShowAdmilModal(false);
      refetchCompanyAdmins();
    } catch (err) {
      toast.error(err?.data?.error || err?.data?.message || err.message);
    }
  };
  const extractHour = (time) => {
    if (time === null || time === undefined) return "";
    const hour24 = Math.floor(time);
    const minute = Math.round((time % 1) * 100);
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const formattedMinute = minute.toString().padStart(2, "0");
    return `${hour12}:${formattedMinute} ${hour24 < 12 ? "AM" : "PM"}`;
  };

  const [editDetails, { editDetailsLoading }] = useEditOutletDetailsMutation();

  const setOuletLocation = async () => {
    let receivedLocation = "https://www.google.com/maps?q=";
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          console.log("location");
          console.log(latitude + "_" + longitude);
          receivedLocation = receivedLocation + latitude + "," + longitude;
          setOtLocation(receivedLocation);
          setLocationError(null);
          const resData = await editDetails({
            outletId,
            // outletLocation: receivedLocation,
            outletLatitude: latitude,
            outletLongitude: longitude,
            token: userInfo?.token,
          });
          // if (resData) toast.success("Location added successfully");
        },
        (err) => {
          console.error(err);
          setLocationError(err.message);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      console.log("inside location else");
      setLocationError("Geolocation is not supported by your browser.");
    }
  };

  const removeOuletLocation = async () => {
    try {
      const resData = await editDetails({
        outletId,
        outletLatitude: 0,
        outletLongitude: 0,
        token: userInfo?.token,
      });
      setOtLocation(null);
      // if (resData) toast.success("Outlet Location removed");
    } catch (err) {
      console.log("error in removeOuletLocation : " + err);
    }
  };
  const toggleLocation = () => {
    if (otLocation) {
      removeOuletLocation();
    } else {
      setOuletLocation();
    }
  };

  return (
    <div className="od-page">
      {!isEditable && (
        <div className="od-topbar">
          <Button
            variant="outline-primary"
            className="od-back-btn"
            onClick={() => navigate("/viewMyOutlets")}
          >
            <FaArrowLeft /> Back
          </Button>
          <div className="od-location-box">
            <button
              onClick={toggleLocation}
              className={`od-location-btn ${otLocation ? "remove" : "set"}`}
            >
              <FaMapMarkerAlt />
              {otLocation ? "Remove Location" : "Set Location"}
            </button>
            {otLocation && (
              <a
                className="od-location-link"
                href={otLocation}
                target="_blank"
                rel="noreferrer"
              >
                📍 See Location
              </a>
            )}
            {locationError && (
              <div className="od-location-error">{locationError}</div>
            )}
          </div>
        </div>
      )}

      {isEditable ? null : isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message ||
            error?.error ||
            "Unable to load outlet details"}
        </Message>
      ) : (
        <>
          <div className="od-hero">
            <h2 className="od-hero-name">{outletDetails?.outletName}</h2>
            <div className="od-hero-sub">
              {userInfo?.companyId?.companyName || "No company"}
              {outletDetails?.outletCategory
                ? ` · ${outletDetails.outletCategory}`
                : ""}
            </div>
            <div className="od-badges">
              <span
                className={`od-badge ${
                  outletDetails?.currentlyOpen
                    ? "od-badge-open"
                    : "od-badge-closed"
                }`}
              >
                {outletDetails?.currentlyOpen
                  ? "Currently Open"
                  : "Currently Closed"}
              </span>
              <span
                className={`od-badge ${
                  outletDetails?.operationStatus
                    ? "od-badge-open"
                    : "od-badge-closed"
                }`}
              >
                {outletDetails?.operationStatus
                  ? "In Operation"
                  : "Not In Operation"}
              </span>
              {outletDetails?.outletGroup && (
                <span className="od-badge od-badge-neutral">
                  Group: {outletDetails.outletGroup}
                </span>
              )}
              {outletDetails?.restrictedOutlet && (
                <span className="od-badge od-badge-closed">
                  Restricted Outlet
                </span>
              )}
              {outletDetails?.wholeSaleOutlet && (
                <span className="od-badge od-badge-neutral">
                  Wholesale Outlet
                </span>
              )}
            </div>
          </div>

          {(canAccess(userInfo, "edit_outlet_details") ||
            isOwner(userInfo)) && (
            <div className="od-actions">
              {canAccess(userInfo, "edit_outlet_details") && (
                <Button variant="primary" onClick={handleEdit}>
                  Edit Outlet Details
                </Button>
              )}
              {/* Only owners manage admins; new ones are created from the
                  All Admins page. */}
              {isOwner(userInfo) && (
                <Button variant="outline-primary" onClick={handleAddAdmin}>
                  Add Admin
                </Button>
              )}
            </div>
          )}

          <div className="od-grid">
            <div className="od-card">
              <div className="od-card-header">
                <FaClock /> Operations
              </div>
              <div className="od-card-body">
                <DetailRow label="Opened Hours">
                  {extractHour(outletDetails?.openingHour)} -{" "}
                  {extractHour(outletDetails?.closingHour)}
                </DetailRow>
                <DetailRow label="Opened Days">
                  {outletDetails?.openedDays?.length > 0
                    ? outletDetails?.openedDays.join(", ")
                    : "Not available"}
                </DetailRow>{" "}
                <DetailRow label="Wholesale Outlet">
                  <YesNoPill value={outletDetails?.wholeSaleOutlet} />
                </DetailRow>
                <DetailRow label="Login Required for Online Order">
                  <YesNoPill value={outletDetails?.loginRequired} />
                </DetailRow>{" "}
                <DetailRow label="Auto Print Online Order">
                  <YesNoPill value={outletDetails?.printOnlineOrder} />
                </DetailRow>
                <DetailRow label="Auto Print Outlet Order">
                  <YesNoPill value={outletDetails?.printOutletOrder} />
                </DetailRow>
                <DetailRow label="Restricted Outlet">
                  <YesNoPill value={outletDetails?.restrictedOutlet} />
                </DetailRow>
              </div>
            </div>

            <div className="od-card">
              <div className="od-card-header">
                <FaMoneyBillWave /> Billing & Fees
              </div>
              <div className="od-card-body">
                <DetailRow label="Separate GST">
                  <YesNoPill value={outletDetails?.separateGst} />
                </DetailRow>
                <DetailRow label="Separate Platform Fee">
                  <YesNoPill value={outletDetails?.separatePlaformFee} />
                </DetailRow>
                <DetailRow label="Platform Fee">
                  {outletDetails?.platformFee ?? "Not available"}
                </DetailRow>
                <DetailRow label="GST Number">
                  {outletDetails?.outletGstNumber || "Not available"}
                </DetailRow>
              </div>
            </div>

            <div className="od-card">
              <div className="od-card-header">
                <FaPhoneAlt /> Contact & Address
              </div>
              <div className="od-card-body">
                <DetailRow label="Contact">
                  {outletDetails?.outletContact || "Not available"}
                </DetailRow>
                <DetailRow label="Secondary Mobiles">
                  {outletDetails?.secondaryContact?.length
                    ? outletDetails.secondaryContact.join(", ")
                    : "Not available"}
                </DetailRow>
                <DetailRow label="Outlet Emails">
                  {outletDetails?.outletEmails?.length
                    ? outletDetails.outletEmails.join(", ")
                    : "Not available"}
                </DetailRow>
                <DetailRow label="State">
                  {outletDetails?.outletState || "Not available"}
                </DetailRow>
                <DetailRow label="Pincode">
                  {outletDetails?.outletPincode || "Not available"}
                </DetailRow>
                <div className="od-row">
                  <span className="od-label">Address</span>
                </div>
                {outletDetails?.outletAddress ? (
                  <div className="od-block-value">
                    {outletDetails.outletAddress}
                  </div>
                ) : (
                  <div className="od-block-value">Not available</div>
                )}
              </div>
            </div>

            <div className="od-card">
              <div className="od-card-header">
                <FaStore /> Outlet Info
              </div>
              <div className="od-card-body">
                <DetailRow label="Company Name">
                  {userInfo?.companyId?.companyName || "No company"}
                </DetailRow>
                <DetailRow label="Outlet Category">
                  {outletDetails?.outletCategory || "Not available"}
                </DetailRow>
                <DetailRow label="Outlet Group">
                  {outletDetails?.outletGroup || "Not available"}
                </DetailRow>

                <DetailRow label="Added By">
                  {outletDetails?.addedBy || "Not available"}
                </DetailRow>
              </div>
            </div>

            <div className="od-card">
              <div className="od-card-header">
                <FaUniversity /> Bank Account Details
              </div>
              <div className="od-card-body">
                {outletDetails?.outletBankDetails ? (
                  <div className="od-block-value">
                    {outletDetails.outletBankDetails}
                  </div>
                ) : (
                  <div className="od-block-value">Not available</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <Modal show={showAdminModal} onHide={handleAdminCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1.05rem" }}>
            <strong>Add Admin to {outletDetails?.outletName}</strong>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ fontSize: "0.88rem", marginBottom: "0.6rem" }}>
            Admins: <strong>{outletCurrentAdmins.length}</strong> of{" "}
            <strong>{outletAdminLimitValue}</strong> allowed
            {outletCurrentAdmins.length > 0 && (
              <div className="text-muted">
                Current:{" "}
                {outletCurrentAdmins.map((admin) => admin.adminName).join(", ")}
              </div>
            )}
          </div>
          {outletAtAdminLimit ? (
            <div style={{ color: "#c0392b", fontSize: "0.88rem" }}>
              This outlet already has the maximum number of admins. Please
              contact Zutle to add more admins.
            </div>
          ) : assignableAdmins.length === 0 ? (
            <div className="text-muted" style={{ fontSize: "0.88rem" }}>
              No other admins are available. Create one from the All Admins
              page.
            </div>
          ) : (
            <Form.Select
              value={adminMobile}
              onChange={(e) => setAdminMobile(e.target.value)}
            >
              <option value="">Select Admin</option>
              {assignableAdmins.map((admin) => (
                <option key={admin._id} value={admin.mobileNumber}>
                  {admin.adminName} ({admin.mobileNumber})
                </option>
              ))}
            </Form.Select>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button
            className={`${userInfo?.themeColor}ThemeHeaderTop`}
            onClick={handleAddAdminSubmit}
            disabled={
              outletAtAdminLimit || !adminMobile || addAdminLoading
            }
          >
            {addAdminLoading ? "Adding..." : "Add Admin"}
          </Button>
          <Button variant="secondary" onClick={handleAdminCancel}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
      {isEditable ? (
        <EditOutletScreen outlet={outletDetails} editable={setIsEditable} />
      ) : (
        <>
          <ReUsableModal
            title="Add Food Count"
            payload={[
              {
                fieldName: "Item Name",
                fieldType: "text",
                fieldValue: itemName,
                setMethod: setItemName,
                disabled: true,
              },
              {
                fieldName: "Existing Quantity",
                fieldType: "number",
                fieldValue: quantity,
                setMethod: setQuantity,
                disabled: true,
              },
              {
                fieldName: "Add Quantity",
                fieldType: "number",
                fieldValue: addQuantity,
                setMethod: setAddQuantity,
              },
              {
                fieldName: "New Quantity",
                fieldType: "number",
                fieldValue: newQuantity,
                setMethod: setNewQuantity,
                disabled: true,
              },
            ]}
            show={showItemCountModal}
            size="md"
            onHide={() => handleCancelEdit(false)}
            onSubmit={submitAddFoodItem}
            submit="Submit"
            cancel="Cancel"
          />
        </>
      )}
    </div>
  );
};

export default OutletDetailsScreen;
